// Geometry for the crafting panel: where its three regions sit, how the tile groups pack into the
// grid, and what the scroll position means for the rail and the pinned group header.
//
// The grid SCROLLS inside a fixed window, and the bench under it never moves -- the panel keeps one
// height whatever is listed or filtered, so nothing the player is reading jumps when the list changes.
// Small groups share a line (Hunting beside Weapons) so a short list fits without scrolling at all.
//
// Pure math, no pixi -- see __tests__/crafting_grid.test.ts.

export const TILE = 36
export const TILE_GAP = 4
// The group's label row above its tiles.
export const HEADER_H = 14
// Between two groups sharing a line, and between lines.
export const GROUP_GAP_X = 12
export const LINE_GAP = 8

export interface Rect {
    x: number
    y: number
    w: number
    h: number
}

export interface GridGroupBox {
    key: string
    // The box's left edge and the top of its line, in grid (content) coordinates.
    x: number
    y: number
    w: number
    tilesY: number
    cols: number
    rows: number
    count: number
    line: number
}

export interface GridLine {
    top: number
    bottom: number
    keys: string[]
}

export interface GridLayout {
    boxes: GridGroupBox[]
    lines: GridLine[]
    contentH: number
}

const tilesWidth = (count: number): number => count * (TILE + TILE_GAP) - TILE_GAP

/** How many tiles fit across a grid this wide. */
export const columnsFor = (width: number): number => Math.max(1, Math.floor((width + TILE_GAP) / (TILE + TILE_GAP)))

/**
 * Pack the groups into lines. A group that fits on the current line beside what is already there joins
 * it; one that does not starts a new line; one too long for a single row takes whole rows of its own.
 * `labelW` is each group's measured label width, which a one-tile group's box must still make room for.
 */
export const layoutGrid = (groups: { key: string; count: number; labelW?: number }[], width: number): GridLayout => {
    const cols = columnsFor(width)
    const boxes: GridGroupBox[] = []
    const lines: GridLine[] = []
    let lineTop = 0
    let lineX = 0
    let lineH = 0
    let lineKeys: string[] = []

    const closeLine = () => {
        if (lineKeys.length) {
            lines.push({ top: lineTop, bottom: lineTop + lineH, keys: lineKeys })
            lineTop += lineH + LINE_GAP
        }
        lineX = 0
        lineH = 0
        lineKeys = []
    }

    groups.forEach(group => {
        const count = Math.max(0, group.count)
        const groupCols = Math.min(cols, Math.max(1, count))
        const rows = Math.max(1, Math.ceil(count / cols))
        const w = Math.min(width, Math.max(tilesWidth(groupCols), group.labelW ?? 0))
        const fits = rows === 1 && lineKeys.length > 0 && lineX + GROUP_GAP_X + w <= width
        if (!fits) {
            closeLine()
        }
        const x = lineKeys.length ? lineX + GROUP_GAP_X : 0
        const h = HEADER_H + rows * (TILE + TILE_GAP) - TILE_GAP
        boxes.push({ key: group.key, x, y: lineTop, w, tilesY: lineTop + HEADER_H, cols: groupCols, rows, count, line: lines.length })
        lineKeys.push(group.key)
        lineX = x + w
        lineH = Math.max(lineH, h)
        // A group of several rows owns its line outright.
        if (rows > 1) {
            closeLine()
        }
    })
    closeLine()
    const last = lines[lines.length - 1]
    return { boxes, lines, contentH: last ? last.bottom : 0 }
}

/** Where tile `index` of a group sits, in grid coordinates. */
export const tileAt = (box: GridGroupBox, index: number): { x: number; y: number } => ({
    x: box.x + (index % box.cols) * (TILE + TILE_GAP),
    y: box.tilesY + Math.floor(index / box.cols) * (TILE + TILE_GAP),
})

export const maxScrollFor = (layout: GridLayout, viewH: number): number => Math.max(0, layout.contentH - viewH)

/** Keep a scroll offset inside its grid, e.g. after the list narrowed or the pack changed. */
export const clampScroll = (scrollY: number, maxScroll: number): number =>
    Math.max(0, Math.min(scrollY, Math.max(0, maxScroll)))

/**
 * The line the player is looking at: the last one whose top has reached the top of the window. At the
 * very bottom it is the last line, however short -- otherwise a short final group could never light
 * its own tab on the rail.
 */
export const lineAt = (layout: GridLayout, scrollY: number, viewH: number): number => {
    if (layout.lines.length < 1) {
        return -1
    }
    if (scrollY > 0 && scrollY >= maxScrollFor(layout, viewH) - 1) {
        return layout.lines.length - 1
    }
    let found = 0
    layout.lines.forEach((line, i) => {
        if (line.top <= scrollY + 1) {
            found = i
        }
    })
    return found
}

/** The rail's lit tab: the first group on the line in view. */
export const activeGroup = (layout: GridLayout, scrollY: number, viewH: number): string | undefined =>
    layout.lines[lineAt(layout, scrollY, viewH)]?.keys[0]

/**
 * The line whose headers should be pinned to the top of the window: one whose own headers have
 * scrolled out of sight while its tiles are still showing. Undefined when the line in view still shows
 * its own headers.
 */
export const pinnedLine = (layout: GridLayout, scrollY: number): GridLine | undefined =>
    layout.lines.find(line => line.top < scrollY - 1 && line.bottom > scrollY + HEADER_H)

/** The scroll that puts a group's line at the top of the window. */
export const scrollForGroup = (layout: GridLayout, key: string, viewH: number): number => {
    const box = layout.boxes.find(b => b.key === key)
    return box ? Math.min(layout.lines[box.line].top, maxScrollFor(layout, viewH)) : 0
}

/**
 * The smallest scroll that shows a tile whole, with its group's label above it when the tile is the
 * top row. Unchanged when it is already in view.
 */
export const scrollToShow = (layout: GridLayout, key: string, index: number, scrollY: number, viewH: number): number => {
    const box = layout.boxes.find(b => b.key === key)
    if (!box) {
        return scrollY
    }
    const { y } = tileAt(box, index)
    const top = index < box.cols ? box.y : y - 2
    const bottom = y + TILE + 2
    let next = scrollY
    if (top < scrollY) {
        next = top
    } else if (bottom > scrollY + viewH) {
        next = bottom - viewH
    }
    return Math.max(0, Math.min(next, maxScrollFor(layout, viewH)))
}

/** Is any of this tile inside the window? A tile half off an edge still counts: it can be seen. */
export const tileVisible = (tileY: number, scrollY: number, viewH: number): boolean =>
    tileY + TILE > scrollY && tileY < scrollY + viewH

// ---- the panel's regions ------------------------------------------------------------------------

// The title band; everything below starts here.
export const BODY_TOP = 46
export const RAIL_H = 20
// The bench's rows: the product and its words (the well beside a name, two lines of description and
// the kind picker), what it needs, and the action.
export const BENCH_WELL = 52
export const BENCH_TOP_H = 58
export const BENCH_ING = 30
export const BENCH_ACTION_H = 28
export const BENCH_PAD = 8
export const BENCH_ROW_GAP = 6
export const BENCH_H = BENCH_PAD * 2 + BENCH_TOP_H + BENCH_ING + BENCH_ACTION_H + BENCH_ROW_GAP * 2

export interface CraftingRegions {
    rail: Rect
    grid: Rect
    bench: Rect
}

/**
 * Where the rail, the grid window and the bench sit. On the dock the bench is a band across the
 * bottom; on the wide (mobile) panel it is a column on the right, beside the grid.
 */
export const craftingRegions = ({
    panelW,
    bottom,
    horizontal,
    margin = 16,
}: {
    panelW: number
    // The last y the panel's content may use.
    bottom: number
    horizontal: boolean
    margin?: number
}): CraftingRegions => {
    if (horizontal) {
        const columnW = (panelW - margin * 3) / 2
        const rail = { x: margin, y: BODY_TOP, w: columnW, h: RAIL_H }
        const gridY = rail.y + RAIL_H + 6
        return {
            rail,
            grid: { x: margin, y: gridY, w: columnW, h: Math.max(TILE, bottom - gridY) },
            bench: { x: margin * 2 + columnW, y: BODY_TOP, w: columnW, h: Math.max(BENCH_H, bottom - BODY_TOP) },
        }
    }
    const w = panelW - margin * 2
    const bench = { x: margin, y: bottom - BENCH_H, w, h: BENCH_H }
    const rail = { x: margin, y: BODY_TOP, w, h: RAIL_H }
    const gridY = rail.y + RAIL_H + 6
    return { rail, grid: { x: margin, y: gridY, w, h: Math.max(TILE, bench.y - 6 - gridY) }, bench }
}
